/**
 * @fileoverview Custom MUI theme configuration with global text color
 * customisation for both light and dark modes and layout constants.
 *
 * Font injection is handled separately via injectCustomFonts() to avoid
 * side effects at module import time.
 */

import { createTheme } from '@mui/material/styles';

/**
 * CSS for custom font-face declaration.
 * Injected into document head via injectCustomFonts().
 */
const FONT_FACE_CSS = `
@font-face {
  font-family: 'Atlassian Sans';
  src: url('/AtlassianSans-latin.woff2') format('woff2');
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}
`;

let fontsInjected = false;

/**
 * Injects custom font-face declarations into the document head.
 * Safe to call multiple times; subsequent calls are no-ops.
 * Only executes in browser environment.
 */
export function injectCustomFonts(): void {
  if (fontsInjected || typeof document === 'undefined') {
    return;
  }

  const style = document.createElement('style');
  style.textContent = FONT_FACE_CSS;
  document.head.appendChild(style);
  fontsInjected = true;
}

export const customTheme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data-toolpad-color-scheme',
  },
  typography: {
    fontFamily: '"Atlassian Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontWeightRegular: 400,
  },
  colorSchemes: {
    light: {
      palette: {
        text: {
          primary: '#292A2E',
          secondary: '#5E6066',
        },
        action: {
          active: '#292A2E',
        },
      },
    },
    dark: {
      palette: {
        text: {
          primary: '#CECFD2',
          secondary: '#96999E',
        },
        action: {
          active: '#CECFD2',
        },
      },
    },
  },
  components: {
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '46px',
          '@media (min-width: 600px)': {
            minHeight: '46px',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          '& .MuiListItemText-root': {
            fontSize: '0.875rem',
            '& .MuiTypography-root': {
              fontSize: '0.875rem',
            },
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          '& .MuiList-root': {
            padding: 0,
          },
        },
      },
    },
  },
});
